import React, { useState, useEffect } from "react";
import { differenceInMilliseconds } from "date-fns";
import loading from "./loading-transparent.gif";

const POLL_MS = 100;

function shouldShow(loadedAt, delay) {
  return differenceInMilliseconds(new Date(), loadedAt) > delay;
}

interface LoadingIndicatorProps {
  /**
   * Timestamp when loading actually started. Allows calculating true elapsed time
   * even if the component renders after loading began, ensuring accurate delay timing.
   */
  loadedAt?: Date;

  /**
   * Delay in milliseconds before the loading indicator is shown.
   * Prevents flashing in fast loading environments. Set to false to show immediately.
   * @default 1000
   */
  delay?: number | false;

  /**
   * Size variant of the loading indicator.
   * @default "regular"
   */
  size?: "small" | "regular";

  /**
   * Custom content to display instead of the default loading GIF.
   * When provided, replaces the built-in loading animation.
   */
  children?: React.ReactNode;
}

/**
 * Loading indicator component with smart delay logic to prevent flashing during fast loads.
 * Uses loadedAt timestamp to calculate true elapsed loading time, even when the component
 * renders after loading has already started. Shows indicator only after the specified delay
 * has passed since the actual loading start time.
 */
export const LoadingIndicator = ({
  loadedAt,
  delay = 1000, // Delay in ms before the loading indicator is shown (to prevent flashing in fast loading envs)
  size = "regular",
  children,
}: LoadingIndicatorProps) => {
  const finalDelay = delay === false ? 0 : delay;
  const [showing, setShowing] = useState(
    loadedAt ? shouldShow(loadedAt, finalDelay) : false
  );

  useEffect(() => {
    if (loadedAt) return () => {};
    const handle = setTimeout(() => setShowing(true), finalDelay);
    return () => clearTimeout(handle);
  }, [delay, finalDelay, loadedAt]);

  useEffect(() => {
    if (!loadedAt) return () => {};
    const handle = setInterval(() => {
      setShowing(shouldShow(loadedAt, finalDelay));
    }, POLL_MS);
    return () => clearInterval(handle);
  }, [delay, finalDelay, loadedAt]);

  if (!showing) return null;
  return (
    <div className="h-full flex flex-col justify-center items-center grow">
      <div className="grow-1" />
      {children}
      {!children && (
        <img
          style={{ height: size == "small" ? 100 : 150 }}
          alt=""
          src={loading}
        />
      )}
      <div className="grow-[1.5]" />
    </div>
  );
};

LoadingIndicator.displayName = "LoadingIndicator";
